import React, { useState, createContext, useContext } from 'react';
import { LoanOfficer, mockLoanOfficers, FullReview, mockManagers, Manager, mockRvps, Rvp } from '../data/mockData';
import { format, subWeeks, startOfWeek, endOfWeek } from 'date-fns';
// Generate last 4 weeks as options
const generateWeeks = () => {
  const weeks = [];
  const today = new Date();
  for (let i = 0; i < 4; i++) {
    const date = subWeeks(today, i);
    const start = startOfWeek(date, {
      weekStartsOn: 1
    });
    const end = endOfWeek(date, {
      weekStartsOn: 1
    });
    weeks.push({
      id: `W${format(start, 'ww-yyyy')}`,
      label: `Week of ${format(start, 'MMM d, yyyy')}`,
      start,
      end
    });
  }
  return weeks;
};
export const availableWeeks = generateWeeks();
interface DataContextType {
  selectedWeek: string;
  setSelectedWeek: (weekId: string) => void;
  loanOfficers: LoanOfficer[];
  managers: Manager[];
  rvps: Rvp[];
  updateReviewField: (loId: string, category: keyof FullReview, field: string, value: any) => void;
  updateReviewCategoryScore: (loId: string, category: keyof FullReview, score: number) => void;
  toggleReviewCategoryCompletion: (loId: string, category: keyof FullReview) => void;
  markReviewComplete: (loId: string) => void;
}
const DataContext = createContext<DataContextType | undefined>(undefined);
export function DataProvider({
  children
}: {
  children: ReactNode;
}) {
  const [selectedWeek, setSelectedWeek] = useState(availableWeeks[0].id);
  const [loanOfficers, setLoanOfficers] = useState<LoanOfficer[]>(mockLoanOfficers);
  const [managers] = useState<Manager[]>(mockManagers);
  const [rvps] = useState<Rvp[]>(mockRvps);
  const updateReviewField = (loId: string, category: keyof FullReview, field: string, value: any) => {
    setLoanOfficers(prev => prev.map(lo => {
      if (lo.id !== loId) return lo;
      // Ensure the week exists for this LO
      const weekData = lo.reviews[selectedWeek] || JSON.parse(JSON.stringify(lo.reviews[Object.keys(lo.reviews)[0]])); // fallback clone
      return {
        ...lo,
        reviews: {
          ...lo.reviews,
          [selectedWeek]: {
            ...weekData,
            [category]: {
              ...weekData[category],
              [field]: value
            }
          }
        }
      };
    }));
  };
  const updateReviewCategoryScore = (loId: string, category: keyof FullReview, score: number) => {
    updateReviewField(loId, category, 'score', score);
    // Recalculate overall score logic could go here
  };
  const toggleReviewCategoryCompletion = (loId: string, category: keyof FullReview) => {
    setLoanOfficers(prev => prev.map(lo => {
      if (lo.id !== loId) return lo;
      const weekData = lo.reviews[selectedWeek] || lo.reviews[Object.keys(lo.reviews)[0]];
      const currentStatus = weekData[category].completed;
      return {
        ...lo,
        reviews: {
          ...lo.reviews,
          [selectedWeek]: {
            ...weekData,
            [category]: {
              ...weekData[category],
              completed: !currentStatus
            }
          }
        }
      };
    }));
  };
  const markReviewComplete = (loId: string) => {
    setLoanOfficers(prev => prev.map(lo => lo.id === loId ? {
      ...lo,
      reviewStatus: 'complete',
      lastReviewDate: format(new Date(), 'yyyy-MM-dd')
    } : lo));
  };
  return <DataContext.Provider value={{
    selectedWeek,
    setSelectedWeek,
    loanOfficers,
    managers,
    rvps,
    updateReviewField,
    updateReviewCategoryScore,
    toggleReviewCategoryCompletion,
    markReviewComplete
  }} data-id="element-309">
      {children}
    </DataContext.Provider>;
}
export function useData() {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
}